import React, { createContext, useEffect, useContext, useState, useCallback } from 'react';
import { useCreateSubscriptionContext } from './createSubscriptionContext';

export const VERSION_CONTEXT_KEYS = {
  VERSION_FORCE_RENDER: 'VERSION_FORCE_RENDER',
  VERSION_SETTINGS_VISIBLE: 'VERSION_SETTINGS_VISIBLE',
  CURRENT_VISIBLE_VERSION: 'CURRENT_VISIBLE_VERSION',
  CURRENT_VISIBLE_TAG: 'CURRENT_VISIBLE_TAG',
  LIFECYCLE_TIMELINE: 'LIFECYCLE_TIMELINE',
  TAGS_LIST: 'TAGS_LIST',
  TIMELINE_LIST: 'TIMELINE_LIST'
};

const INITIAL_STATE = () => ({
  [VERSION_CONTEXT_KEYS.VERSION_FORCE_RENDER]: 0,
  [VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE]: false,
  [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: 'draft',
  [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG]: {},
  [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: {},
  [VERSION_CONTEXT_KEYS.TIMELINE_LIST]: [],
  [VERSION_CONTEXT_KEYS.TAGS_LIST]: [],
});

const ComplexVersionContext = createContext(null);

export const useVersionContext = (selector, customName = '', debug = false) => {
  const store = useContext(ComplexVersionContext);
  if (!store) {
    throw new Error('Store not found');
  }

  const [state, setState] = useState(selector(store.get()));

  useEffect(() => {
    return store.subscribe(
      () => {
        setState(previousState => {
          if (debug) {
            console.log(
              'Change in context :: ',
              customName,
              selector(store.get()),
              previousState,
              previousState === selector(store.get()),
            );
          }
          return selector(store.get(), previousState);
        });
      },
      debug ? customName : '',
    );
  }, []);

  useEffect(() => {
    if (debug) console.log('context change reflected in state :: ', customName, state, store.get());
  }, [state, debug, customName]);

  return [state, store.set, store.reset, store.get];
};

export const VersionContextProvider = ({
  children,
  customState,
  customName = 'VersionContext',
}) => {
  const store = useCreateSubscriptionContext(
    Object.assign({}, INITIAL_STATE(), customState),
    undefined,
    false,
    customName,
  );

  store.reset = useCallback(() => {
    store.set(INITIAL_STATE());
  }, []);

  return <ComplexVersionContext.Provider value={store}>{children}</ComplexVersionContext.Provider>;
};
